/**
 * Vendor → Purchase Orders tab
 * Construction pack — shows all POs issued to this vendor.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useConstructionPack } from "@/lib/useConstructionPack";
import { useQuery } from "@tanstack/react-query";
import { purchaseOrdersListQueryOptions } from "@/lib/queries";
import type { PurchaseOrder } from "@/lib/types";

export const Route = createFileRoute("/_auth/vendors/$vendorId/pos")({
  component: VendorPOsTab,
});

const STATUS_COLOR: Record<string, string> = {
  draft: "bg-slate-100 text-slate-600 dark:bg-gray-800 dark:text-slate-300",
  pending_approval: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
  approved: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  issued: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300",
  partially_received: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  received: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  closed: "bg-slate-200 text-slate-500 dark:bg-gray-700 dark:text-slate-400",
  cancelled: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  voided: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
};

function fmt(n: string | number) {
  const v = parseFloat(String(n));
  return isNaN(v)
    ? "—"
    : v.toLocaleString("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 });
}

function VendorPOsTab() {
  const { enabled: constructionEnabled } = useConstructionPack();
  if (!constructionEnabled) return null;
  const { vendorId } = Route.useParams();
  const posQ = useQuery(purchaseOrdersListQueryOptions({ vendor_id: vendorId }));
  const pos: PurchaseOrder[] = posQ.data ?? [];

  // Summary
  const openPOs = pos.filter((p) => ["draft", "issued", "approved", "pending_approval"].includes(p.status));
  const openValue = openPOs.reduce((s, p) => s + parseFloat(p.total ?? "0"), 0);
  const totalValue = pos.reduce((s, p) => s + parseFloat(p.total ?? "0"), 0);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Purchase Orders
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            {pos.length} total · {openPOs.length} open ·{" "}
            <span className="font-medium text-amber-700 dark:text-amber-400">
              {fmt(openValue)} open value
            </span>{" "}
            · {fmt(totalValue)} lifetime
          </p>
        </div>
        <Link
          to="/purchase-orders"
          className="text-xs text-blue-600 hover:underline dark:text-blue-400"
        >
          All POs →
        </Link>
      </div>

      {posQ.isLoading && (
        <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>
      )}
      {posQ.error && (
        <div className="text-sm text-red-600">
          Failed to load: {(posQ.error as Error).message}
        </div>
      )}

      {!posQ.isLoading && pos.length === 0 && (
        <div className="text-sm text-slate-400 dark:text-slate-500">
          No purchase orders for this vendor.
        </div>
      )}

      {pos.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-gray-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-gray-900 text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2.5">PO #</th>
                <th className="text-left px-3 py-2.5">Type</th>
                <th className="text-left px-3 py-2.5">Status</th>
                <th className="text-left px-3 py-2.5">Issued</th>
                <th className="text-left px-3 py-2.5">Description</th>
                <th className="text-right px-3 py-2.5">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-gray-800 bg-white dark:bg-gray-900">
              {pos.map((po) => (
                <tr
                  key={po.id}
                  className="hover:bg-slate-50 dark:hover:bg-gray-800/50 transition-colors"
                >
                  <td className="px-3 py-2.5">
                    <Link
                      to="/purchase-orders/$poId"
                      params={{ poId: po.id }}
                      className="font-mono text-blue-700 hover:underline dark:text-blue-400"
                    >
                      {po.po_number}
                    </Link>
                  </td>
                  <td className="px-3 py-2.5 text-slate-600 dark:text-slate-400 capitalize">
                    {po.po_type.replace(/_/g, " ")}
                  </td>
                  <td className="px-3 py-2.5">
                    <span
                      className={`px-2 py-0.5 text-xs rounded font-medium ${STATUS_COLOR[po.status] ?? STATUS_COLOR.draft}`}
                    >
                      {po.status.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-slate-500 dark:text-slate-400 text-xs">
                    {po.issue_date ?? "—"}
                  </td>
                  <td className="px-3 py-2.5 text-slate-700 dark:text-slate-300 max-w-xs truncate">
                    {po.description ?? "—"}
                  </td>
                  <td className="px-3 py-2.5 text-right font-medium text-slate-900 dark:text-slate-100">
                    {fmt(po.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
